/** 隐私政策正文（不含页面级滚动门槛逻辑，便于 /privacy 与 /legal 复用） */
export default function PrivacyPageContent() {
  return (
    <>
      <h1 className="text-3xl font-bold mb-2">Privacy Policy</h1>
      <p className="text-gray-500 mb-8">
        Effective Date: January 1, 2026 &nbsp;|&nbsp; Last Updated: September 29, 2026
      </p>

      <div className="space-y-8 text-gray-700 leading-relaxed">
        <section>
          <h2 className="text-xl font-semibold mb-3">1. What Information We Collect</h2>
          <p className="mb-2">
            To provide matching and 30-day challenge services, we collect:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              <strong>Profile information you provide:</strong> nickname, gender, date of birth,
              height, weight, marital status, education, occupation, income range, assets, hometown
              and city of residence, languages, MBTI, self-introduction, family background,
              interests, partner preferences, avatar and photos.
            </li>
            <li>
              <strong>Account and contact data:</strong> email address (or Apple Sign-In identifier)
              and authentication tokens.
            </li>
            <li>
              <strong>Identity documents:</strong> images of a government-issued ID uploaded for age
              and identity verification.
            </li>
            <li>
              <strong>Usage and device data:</strong> device and push notification identifiers,
              online/presence status, matching and daily check-in records, message and call counters
              used to enforce quotas.
            </li>
            <li>
              <strong>Content you send:</strong> chat messages, images and short videos, and any
              screenshots you attach to a report.
            </li>
          </ul>
          <p className="mt-2">
            We do not sell your personal data, and we do not use it for advertising profiling.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-3">
            2. Identity Verification and ID Images
          </h2>
          <p className="mb-2">
            ID images are used solely to verify your legal age and identity. They are stored in
            access-controlled, encrypted object storage (Cloudflare R2) and can be viewed only by
            authorized reviewers for the purpose of that review.
          </p>
          <p>
            ID images are deleted together with your account. We do not use ID images for matching,
            recommendations or any purpose other than verification and legal compliance.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-3">3. Chats, Media, and Calls</h2>
          <p className="mb-2">
            <strong>Text messages</strong> are delivered through real-time channels and are also
            stored on our servers so that they can be delivered reliably. Stored messages are
            automatically and permanently deleted after <strong>7 days</strong> by a scheduled
            server job, and a copy may remain in the local database on your own device until you
            delete the conversation or the app.
          </p>
          <p className="mb-2">
            <strong>Images and short videos</strong> are stored temporarily in our cloud storage and
            permanently destroyed automatically: within <strong>7 days</strong> of being sent when at
            least one person in the conversation has a paid membership or pass, and within{" "}
            <strong>24 hours</strong> when both people use the free plan. Sending short videos is
            available to paid members only. Messages sent in "disappearing" mode are additionally
            hidden in the app shortly after being opened.
          </p>
          <p>
            <strong>Voice and video calls</strong> are transmitted through a third-party real-time
            media provider (Agora). We do not record call audio or video; we store only metadata
            such as call duration, which is required to enforce daily limits and billing rules.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-3">4. AI Chat Assistance</h2>
          <p className="mb-2">
            The optional "AI Assist" feature sends the following to a third-party large language
            model provider (DeepSeek) to generate opening lines, rewrites and topic suggestions:
            the display name and public profile introduction of the person you are chatting with,
            and the draft text you choose to polish.
          </p>
          <p>
            AI Assist is used only when you open it. Your full chat history is not sent. If you do
            not wish to share this data with the provider, do not use the feature; the app also
            offers built-in offline suggestions that are generated on your device.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-3">5. Payments and Subscriptions</h2>
          <p className="mb-2">
            Within the app, digital entitlements (VIP memberships, day passes and challenge fees)
            are sold exclusively through Apple App Store In-App Purchase on iOS and Google Play
            Billing on Android. We do not operate web or card checkout and never receive or store
            your card number.
          </p>
          <p>
            VIP monthly memberships are <strong>auto-renewable subscriptions</strong>: they renew
            automatically unless cancelled at least 24 hours before the end of the current period,
            and can be managed in your App Store or Google Play account settings. We receive from
            the stores only the transaction identifier, product identifier and purchase status
            needed to grant and audit your entitlements.
          </p>
          <p className="mt-2">
            <strong>Crowdfunding supporter codes.</strong> When you redeem a supporter code, we
            record which account redeemed it, when, and how many VIP days were added. To help with
            lost codes we may store the backer number issued by the crowdfunding platform next to a
            code; we do not store your name or email address from the campaign. Codes are stored
            only as one-way hashes, and rate-limiting logs keep a hashed form of your IP address
            for up to 90 days to prevent code guessing.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-3">6. Report, Block, and Safety</h2>
          <p className="mb-2">
            You can report or block any user in the app. Blocked users no longer appear in your
            recommendations or conversation list. When reporting you may attach up to 3 screenshots
            as evidence.
          </p>
          <p>
            Reports and attached evidence are used only for manual review and enforcement. They are
            retained while the report is being handled and for a limited period afterwards as a
            record of the enforcement decision, and are deleted when the reporting account is
            deleted.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-3">7. Third Parties We Share Data With</h2>
          <p className="mb-2">
            We use the following processors, each limited to the purpose listed:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Supabase:</strong> database, authentication and server functions hosting.</li>
            <li><strong>Cloudflare R2:</strong> storage of avatars, photos, chat media and ID images.</li>
            <li><strong>Cloudflare Turnstile:</strong> bot protection during sign-up and login.</li>
            <li><strong>Agora:</strong> real-time voice and video call transmission (no recording).</li>
            <li><strong>DeepSeek:</strong> AI chat suggestions, only when you use AI Assist (see Section 4).</li>
            <li><strong>Apple / Google:</strong> in-app purchases, and push notification delivery (APNs / Firebase Cloud Messaging).</li>
            <li><strong>Sentry:</strong> crash and error diagnostics.</li>
          </ul>
          <p className="mt-2">
            These providers operate servers in various countries, so your data may be transferred
            and processed outside your country of residence. We do not share your personal data with
            advertisers or data brokers.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-3">8. Retention Summary</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li>Chat messages: deleted from our servers after 7 days.</li>
            <li>Chat images and short videos: destroyed within 7 days (conversations with a paid member) or 24 hours (both on the free plan).</li>
            <li>Profile, photos and ID images: kept while your account exists, deleted on account deletion.</li>
            <li>Reports and evidence: kept for the review and a limited enforcement record period.</li>
            <li>Payment records: retained as required for accounting and tax purposes, in a form that is not used to identify you for other purposes.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-3">9. Account Deletion and Your Rights</h2>
          <p className="mb-2">
            You can delete your account in the mobile app via Settings → Delete Account. Deletion
            removes your login identity, profile, photos and ID images from our servers. Chat
            history stored locally on your device is removed with the app data. Minimum information
            required by law (such as financial audit records) may be retained.
          </p>
          <p className="mb-2">
            Subject to your local law, you may request access to, correction of, or deletion of your
            personal data, object to certain processing, or withdraw consent. You can exercise most
            of these rights directly in the app (edit profile, delete account) or contact us using
            the details in Section 12.
          </p>
          <p>
            Deleting your account does not cancel an auto-renewable subscription; you must cancel it
            in your App Store or Google Play account settings.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-3">10. Notifications</h2>
          <p>
            With your permission we send push notifications for incoming calls, new messages and
            match events. You can turn notifications off at any time in your device settings.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-3">11. Age Restriction</h2>
          <p>
            This service is intended for users who are at least 18 years old. We use identity and age
            verification to reduce the risk of use by minors. If we learn that a minor has created an
            account, we delete it.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-3">12. Contact Us</h2>
          <p className="mb-2">
            For questions about this Privacy Policy or your personal information, use the "Contact
            Us" feature in the app or email us.
          </p>
          <p>
            Personal Information Manager: プ　ユエ <br />
            Email: bamabang.info@gmail.com
          </p>
        </section>
      </div>
    </>
  );
}
